import * as React from "react"
import { renderMarkdown } from "../projects/aegis/markdown"

const COMMENT_HEADINGS: Record<number, number> = { 1: 16, 2: 15, 3: 14, 4: 13, 5: 13, 6: 12 }

function LayerInfoIcon({ text }: { text: string }) {
  const anchorRef = React.useRef<HTMLSpanElement>(null)
  const hideTimer = React.useRef<number | null>(null)
  const [tip, setTip] = React.useState<{ top: number, left: number } | null>(null)

  const clearHide = () => {
    if (hideTimer.current != null) window.clearTimeout(hideTimer.current)
    hideTimer.current = null
  }

  const show = () => {
    clearHide()
    const icon = anchorRef.current
    if (!icon) return
    const row = icon.closest("[data-public-layer-row]")
    const panel = icon.closest(".public-layers-slot")
    const rowRect = (row ?? icon).getBoundingClientRect()
    const panelRect = panel?.getBoundingClientRect()
    const top = Math.max(8, Math.min(rowRect.top, window.innerHeight - 72))
    const left = (panelRect?.right ?? rowRect.right) + 10
    setTip({ top, left })
  }

  const scheduleHide = () => {
    clearHide()
    hideTimer.current = window.setTimeout(() => setTip(null), 150)
  }

  React.useEffect(() => () => clearHide(), [])

  return (
    <span
      ref={anchorRef}
      className="public-layer-info"
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
      onFocus={show}
      onBlur={scheduleHide}
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
    >
      <i className="fas fa-info-circle" aria-label="Layer comment" />
      {tip && (
        <div
          className="public-layer-info__tip"
          role="tooltip"
          style={{ top: tip.top, left: tip.left }}
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
        >
          {renderMarkdown(text, undefined, { headingSizes: COMMENT_HEADINGS })}
        </div>
      )}
    </span>
  )
}

type LayerListRowProps = {
  name: string
  iconClassName: string
  iconColor?: string
  visible: boolean
  selected?: boolean
  /** Chat is pointing at this layer. Distinct from selection. */
  highlighted?: boolean
  dimmed?: boolean
  title?: string
  onSelect: (event: React.MouseEvent<HTMLDivElement>) => void
  onToggleVisibility: (event: React.MouseEvent<HTMLSpanElement>) => void
  /** When set, a trash control is shown (e.g. remove layer from stack). */
  onDelete?: (event: React.MouseEvent<HTMLElement>) => void
  deleteTitle?: string
  /** Public map card: checkbox and colour swatch instead of the editor icon row. */
  appearance?: "default" | "checkbox"
  /** Public map: this layer is drawn above the others. */
  raised?: boolean
  /** Public map: comment shown when the info icon is hovered. */
  info?: string
}

export const LayerListRow: React.FC<LayerListRowProps> = ({
  name,
  iconClassName,
  iconColor = "rgb(96,96,96)",
  visible,
  selected = false,
  highlighted = false,
  dimmed = false,
  title,
  onSelect,
  onToggleVisibility,
  onDelete,
  deleteTitle = "Remove layer",
  appearance = "default",
  raised = false,
  info,
}) => {
  if (appearance === "checkbox") {
    return (
      <div
        className="d-flex align-items-center px-3 py-2"
        data-public-layer-row=""
        style={{
          cursor: "pointer",
          color: dimmed ? "#c0392b" : "#243044",
          textDecoration: dimmed ? "line-through" : "none",
          backgroundColor: selected ? "#d5e2fb" : highlighted ? "#fff8e8" : "transparent",
          boxShadow: selected ? "inset 3px 0 0 #3b4a87" : highlighted ? "inset 3px 0 0 #f0ad4e" : undefined,
          fontWeight: selected ? 600 : undefined,
        }}
        title={title}
        onClick={onSelect}
      >
        <input
          type="checkbox"
          className="mr-2"
          checked={visible}
          aria-label={visible ? `Hide ${name}` : `Show ${name}`}
          onClick={event => event.stopPropagation()}
          onChange={event => onToggleVisibility(event as unknown as React.MouseEvent<HTMLSpanElement>)}
        />
        <span
          aria-hidden
          style={{
            width: 14,
            height: 14,
            borderRadius: 3,
            background: iconColor,
            flexShrink: 0,
            boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.12)",
          }}
        />
        <span className="ml-2 flex-grow-1" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.92rem" }}>
          {name}
        </span>
        {raised && (
          <i
            className="fas fa-arrow-up ml-2"
            title="Shown on top"
            aria-label="Shown on top"
            style={{ color: "#3b4a87", fontSize: 11, flexShrink: 0 }}
          />
        )}
        {info && <LayerInfoIcon text={info} />}
      </div>
    )
  }
  return (
    <div
      className={selected ? "d-flex align-items-center bg-primary text-white px-3 py-2" : "align-items-center d-flex px-3 py-2"}
      style={{
        cursor: "pointer",
        color: dimmed ? "red" : "inherit",
        textDecoration: dimmed ? "line-through" : "none",
        boxShadow: highlighted && !selected ? "inset 4px 0 0 #f0ad4e" : undefined,
        backgroundColor: highlighted && !selected ? "#fff3cd" : undefined,
      }}
      title={title}
      onClick={onSelect}
    >
      <div>
        <i className={`fas fa-fw ${iconClassName}`} style={{ color: iconColor, textShadow: "1px 1px 1px black" }} />
      </div>
      <span className="ml-2 mr-3 flex-grow-1" style={{ overflowX: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {name}
      </span>
      {onDelete && (
        <span
          title={deleteTitle}
          className="flex-shrink-0 mr-2"
          style={{ cursor: "pointer" }}
          onClick={event => {
            event.stopPropagation()
            onDelete(event)
          }}
          onMouseDown={event => event.stopPropagation()}
        >
          <i className={`fas fa-fw fa-trash-alt ${selected ? "text-white" : "text-danger"}`} />
        </span>
      )}
      <span onClick={onToggleVisibility}>
        {visible ? <i className="fas fa-fw fa-eye" /> : <i className="fas fa-fw fa-eye-slash" />}
      </span>
    </div>
  )
}
